// ElectricalLoadCalc — mirror of PlumbingDiagnose but for AI Load-Calc.
// Drag-and-drop panel/nameplate photo, description, optional job attach.
// Verdict shows service size recommendation, upgrade Y/N, per-load VA
// breakdown, NEC-cited notes, and a customer estimate.
import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminShell } from "@/components/Layout";
import { toast } from "sonner";
import {
  Lightning, Sparkle, Warning, CheckCircle, ArrowRight, X, Camera,
  UploadSimple, Image as ImageIcon, ShieldWarning, ArrowUpRight, Gauge,
} from "@phosphor-icons/react";

const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const MAX_MB = 8;
const CONFIDENCE = {
  low:    { cls: "bg-amber-100 text-amber-800",   label: "LOW CONFIDENCE" },
  medium: { cls: "bg-slate-100 text-slate-700",   label: "MEDIUM CONFIDENCE" },
  high:   { cls: "bg-emerald-100 text-emerald-800", label: "HIGH CONFIDENCE" },
};

export default function ElectricalLoadCalc() {
  const [form, setForm] = useState({ description: "", address: "", job_id: "" });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);
  const fileRef = useRef(null);
  const cameraRef = useRef(null);

  const loadJobs = async () => {
    try { const r = await api.get("/electrical/electrical-jobs"); setJobs(r.data.jobs || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { loadJobs(); }, []);

  const handleFile = (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) { toast.error("Only image files"); return; }
    if (f.size > MAX_MB * 1024 * 1024) { toast.error(`Photo too large (max ${MAX_MB} MB)`); return; }
    setPhoto(f);
    const reader = new FileReader();
    reader.onload = e => setPhotoPreview(e.target.result);
    reader.readAsDataURL(f);
  };
  const clearPhoto = () => { setPhoto(null); setPhotoPreview(null); if (fileRef.current) fileRef.current.value = ""; };

  const run = async () => {
    if (form.description.trim().length < 6) { toast.error("Add a short description first"); return; }
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append("description", form.description.trim());
      if (form.address.trim()) fd.append("address", form.address.trim());
      if (form.job_id) fd.append("job_id", form.job_id);
      if (photo) fd.append("photo", photo);
      const r = await api.post("/electrical/ai-load-calc", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(r.data);
      if (r.data.saved_to_job) {
        const j = jobs.find(x => x.id === r.data.saved_to_job);
        toast.success(`Saved to job: ${j?.title || "job"}`);
        await loadJobs();
      } else {
        toast.success(`Load-Calc — ${r.data.recommended_service_amps}A recommended`);
      }
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Load-Calc failed")); }
    finally { setLoading(false); }
  };

  const clear = () => { setForm({ description: "", address: "", job_id: "" }); setResult(null); clearPhoto(); };
  const conf = result ? (CONFIDENCE[result.confidence] || CONFIDENCE.medium) : null;

  return (
    <AdminShell>
      <div className="p-4 sm:p-6 max-w-3xl mx-auto" data-testid="electrical-load-calc-page">
        <div className="flex items-center gap-3 mb-4">
          <div className="h-11 w-11 rounded-xl bg-yellow-500/15 text-yellow-600 flex items-center justify-center">
            <Lightning weight="fill" size={22}/>
          </div>
          <div>
            <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight" data-testid="load-calc-heading">
              AI Load-Calc
            </h1>
            <div className="text-xs sm:text-sm text-slate-500 font-medium">
              Snap the panel + big-appliance nameplates → Claude Vision returns an NEC-220 load calc + service size.
            </div>
          </div>
        </div>

        <div className="bg-white border-2 border-slate-200 rounded-xl p-4 space-y-3" data-testid="load-calc-form">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">Photo (recommended — Claude Vision)</div>
            {photoPreview ? (
              <div className="relative border-2 border-yellow-500 rounded-lg overflow-hidden" data-testid="photo-preview">
                <img src={photoPreview} alt="preview" className="w-full max-h-64 object-contain bg-slate-50"/>
                <button type="button" onClick={clearPhoto}
                  data-testid="photo-clear"
                  className="absolute top-2 right-2 h-8 w-8 rounded-full bg-white/90 hover:bg-white text-slate-800 inline-flex items-center justify-center shadow">
                  <X size={14}/>
                </button>
                <div className="absolute bottom-2 left-2 text-[10px] font-black uppercase tracking-widest bg-yellow-500 text-slate-900 px-2 py-1 rounded inline-flex items-center gap-1">
                  <ImageIcon weight="fill" size={11}/> {photo?.name || "photo"}
                </div>
              </div>
            ) : (
              <div
                onDragOver={e => { e.preventDefault(); setDragging(true); }}
                onDragLeave={() => setDragging(false)}
                onDrop={e => { e.preventDefault(); setDragging(false); const f = e.dataTransfer?.files?.[0]; if (f) handleFile(f); }}
                data-testid="photo-dropzone"
                className={`rounded-lg border-2 border-dashed p-6 text-center transition ${
                  dragging ? "border-yellow-500 bg-yellow-50" : "border-slate-300 bg-slate-50"}`}>
                <UploadSimple size={26} className="mx-auto text-slate-500 mb-1.5"/>
                <div className="text-sm font-black text-slate-700">Drop panel or nameplate photo, or pick one below</div>
                <div className="text-[11px] text-slate-500 mt-0.5">JPG · PNG · HEIC — max {MAX_MB} MB</div>
                <div className="mt-3 flex gap-2 justify-center flex-wrap">
                  <button type="button" onClick={(e) => { e.stopPropagation(); cameraRef.current?.click(); }}
                    data-testid="photo-take-btn"
                    className="h-9 px-3 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-slate-900 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                    Take photo
                  </button>
                  <button type="button" onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                    data-testid="photo-pick-btn"
                    className="h-9 px-3 rounded-lg bg-white border-2 border-slate-200 hover:border-slate-900 text-slate-800 text-[11px] font-black uppercase tracking-widest inline-flex items-center gap-1.5">
                    Choose from library
                  </button>
                </div>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/*"
              className="hidden" data-testid="photo-file-input"
              onChange={e => handleFile(e.target.files?.[0])}/>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment"
              className="hidden" data-testid="photo-camera-input"
              onChange={e => handleFile(e.target.files?.[0])}/>
          </div>

          <Field label="Job description*">
            <textarea value={form.description} onChange={e => setForm({ ...form, description: e.target.value })}
              placeholder="e.g. Adding 60A EV charger + upgrading electric range. Existing 100A panel. Photos of nameplates attached."
              rows={3} data-testid="load-calc-desc"
              className="w-full px-3 py-2 border-2 border-slate-200 rounded-lg font-medium text-sm resize-y"/>
          </Field>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Field label="Address (optional)">
              <input type="text" value={form.address} onChange={e => setForm({ ...form, address: e.target.value })}
                placeholder="3421 Girard Ave, Philadelphia PA"
                data-testid="load-calc-address"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium"/>
            </Field>
            <Field label="Attach to a job (optional)">
              <select value={form.job_id} onChange={e => setForm({ ...form, job_id: e.target.value })}
                data-testid="load-calc-job"
                className="w-full h-11 px-3 border-2 border-slate-200 rounded-lg font-medium">
                <option value="">— No job (one-off)</option>
                {jobs.map(j => <option key={j.id} value={j.id}>{j.has_load_calc ? "✓ " : ""}{j.title} — {j.address}</option>)}
              </select>
            </Field>
          </div>

          <div className="flex items-center gap-2">
            <button type="button" onClick={run} disabled={loading}
              data-testid="load-calc-run"
              className="flex-1 h-11 rounded-lg bg-yellow-500 hover:bg-yellow-600 text-slate-900 font-black uppercase text-xs tracking-widest inline-flex items-center justify-center gap-2 disabled:opacity-60">
              <Sparkle weight="fill" size={14}/> {loading ? "Calculating…" : "Run AI Load-Calc"}
            </button>
            {(result || photo) && (
              <button type="button" onClick={clear}
                data-testid="load-calc-clear"
                className="h-11 px-3 rounded-lg border-2 border-slate-200 hover:border-slate-400 text-slate-700 font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                <X size={12}/> Clear
              </button>
            )}
          </div>
        </div>

        {result && (
          <div className="mt-4 bg-white border-2 border-yellow-200 rounded-xl overflow-hidden" data-testid="load-calc-result">
            {/* Verdict header */}
            <div className="bg-gradient-to-br from-slate-900 to-yellow-900 text-white p-4">
              <div className="flex items-start justify-between gap-2 mb-2 flex-wrap">
                <div className="flex items-center gap-2">
                  <Sparkle weight="fill" size={16}/>
                  <div className="text-[10px] font-black uppercase tracking-widest">Claude Haiku · NEC 220 Load-Calc</div>
                  {result.used_photo && (
                    <span className="text-[9px] font-black uppercase tracking-widest bg-white/10 text-yellow-100 px-2 py-0.5 rounded inline-flex items-center gap-1">
                      <Camera weight="fill" size={9}/> Photo used
                    </span>
                  )}
                </div>
                <span className={`text-[9px] font-black uppercase tracking-widest px-2 py-0.5 rounded ${conf.cls}`}
                        data-testid="load-calc-confidence">
                  {conf.label}
                </span>
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-3">
                <MetricBlock label="Existing" value={`${result.existing_service_amps ?? "?"}A`}
                  sub={`${result.existing_voltage}V`} testid="load-calc-existing"/>
                <MetricBlock label="Calculated" value={`${result.calculated_amps}A`}
                  sub={`${result.total_load_va.toLocaleString()} VA`} testid="load-calc-calc"/>
                <MetricBlock label="Recommended" value={`${result.recommended_service_amps}A`}
                  accent={result.service_upgrade_needed ? "amber" : "emerald"}
                  sub={result.service_upgrade_needed ? "UPGRADE NEEDED" : "Adequate"}
                  testid="load-calc-recommended"/>
              </div>

              {result.est_cents > 0 && (
                <div className="mt-3">
                  <div className="text-[10px] font-black uppercase tracking-widest text-yellow-200">Customer estimate</div>
                  <div className="font-display font-black text-3xl" data-testid="load-calc-est">{dollars(result.est_cents)}</div>
                </div>
              )}
              {result.saved_to_job && (
                <div className="mt-3 rounded bg-emerald-500/20 border border-emerald-400/40 text-emerald-100 text-[11px] font-black uppercase tracking-widest px-2 py-1 inline-flex items-center gap-1" data-testid="load-calc-saved">
                  <CheckCircle weight="fill" size={11}/> Saved to job — description updated
                </div>
              )}
            </div>

            <div className="p-4 space-y-3">
              {result.safety_flag && (
                <div className="rounded-lg bg-red-50 border border-red-200 p-3 flex items-start gap-2" data-testid="load-calc-safety">
                  <ShieldWarning weight="fill" size={16} className="text-red-600 shrink-0 mt-0.5"/>
                  <div className="text-xs text-red-900 leading-relaxed">
                    <span className="font-black uppercase text-[10px] tracking-widest mr-1">Safety</span>
                    {result.safety_flag}
                  </div>
                </div>
              )}

              {result.load_breakdown.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1">
                    <Gauge size={12}/> Load breakdown
                  </div>
                  <div className="border-2 border-slate-200 rounded-lg divide-y divide-slate-100" data-testid="load-calc-breakdown">
                    {result.load_breakdown.map((l, i) => (
                      <div key={i} className="p-2 flex items-center gap-2">
                        <div className="flex-1 min-w-0 text-sm text-slate-800 truncate">{l.label}</div>
                        <div className="font-mono text-sm text-slate-900">{l.va.toLocaleString()} VA</div>
                      </div>
                    ))}
                    <div className="p-2 flex items-center gap-2 bg-slate-50">
                      <div className="flex-1 text-[10px] font-black uppercase tracking-widest text-slate-600">TOTAL</div>
                      <div className="font-mono text-sm font-black text-slate-900">{result.total_load_va.toLocaleString()} VA</div>
                    </div>
                  </div>
                </div>
              )}

              {result.nec_notes.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1">
                    <ArrowUpRight size={12}/> NEC-cited notes
                  </div>
                  <ul className="space-y-1" data-testid="load-calc-nec">
                    {result.nec_notes.map((n, i) => (
                      <li key={i} className="text-sm text-slate-800 leading-snug flex items-start gap-2">
                        <span className="text-yellow-600 mt-1">§</span> {n}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {result.next_steps.length > 0 && (
                <div>
                  <div className="text-[10px] font-black uppercase tracking-widest text-slate-500 mb-1.5 inline-flex items-center gap-1">
                    <ArrowRight size={12}/> Next steps
                  </div>
                  <ol className="space-y-1.5" data-testid="load-calc-steps">
                    {result.next_steps.map((s, i) => (
                      <li key={i} className="text-sm text-slate-800 flex items-start gap-2">
                        <span className="h-5 w-5 rounded-md bg-slate-100 text-slate-700 flex items-center justify-center font-black text-[10px] shrink-0 mt-0.5">{i+1}</span>
                        <span className="leading-snug">{s}</span>
                      </li>
                    ))}
                  </ol>
                </div>
              )}

              <div className="pt-2 border-t border-slate-100 text-[10px] text-slate-400 leading-snug">
                AI-generated load-calc — always verify against field-measured demand + local AHJ before pulling a permit.
                Field Radar is a diagnostic assist tool, not a substitute for a licensed electrician's judgment.
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminShell>
  );
}

function MetricBlock({ label, value, sub, accent = "slate", testid }) {
  const c = {
    slate: "text-white",
    emerald: "text-emerald-300",
    amber: "text-amber-300",
  }[accent] || "text-white";
  return (
    <div data-testid={testid}>
      <div className="text-[9px] font-black uppercase tracking-widest text-yellow-200">{label}</div>
      <div className={`font-display font-black text-2xl leading-tight ${c}`}>{value}</div>
      <div className="text-[10px] text-yellow-100/80 mt-0.5">{sub}</div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <div className="text-[10px] font-black uppercase tracking-widest text-slate-600 mb-1">{label}</div>
      {children}
    </label>
  );
}
